import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react';

import invariant from 'tiny-invariant';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ANALYTICS_CHANNEL, NAV_STATE_LS_KEY } from '../../../common/constants';

import type {
	CollapseCallback,
	ExpandCallback,
	ResizeEndCallback,
	ResizeStartCallback,
	SideNavState,
	SideNavVisibilityContextType,
	SideNavVisibilityProviderProps,
} from './types';

export const SideNavVisibilityContext = createContext<SideNavVisibilityContextType | null>(null);

/*
 * useSideNavVisibility - Returns Visibility callbacks from Navigation System
 */
export const useSideNavVisibility = () => {
	const context = useContext(SideNavVisibilityContext);
	invariant(context, 'useSideNavVisibility must be used within an SideNavVisibilityProvider');

	return context;
};

/*
 *  SideNavVisibilityProvider - Wrapper for the Navigation System visibility callbacks.
 */
export function SideNavVisibilityProvider({
	defaultCollapsed = false,
	defaultWidth = 320,
	onCollapse,
	onExpand,
	onResizeStart,
	onResizeEnd,
	children,
}: SideNavVisibilityProviderProps) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Access sideNavState in localStorage or set an initial state from props.
	// TODO: Read the state from AGG instead. I like the fact that all of this code is together,
	//  but a pure Relay approach would not use a Context, but instead inline this code into
	//  SideNavigationLayout.
	const initState = useMemo(() => {
		const storedState = getLocalStorageState(localStorage.getItem(NAV_STATE_LS_KEY));
		if (storedState) {
			return storedState;
		}

		const defaultState: SideNavState = {
			defaultWidth,
			defaultCollapsed,
		};

		setLocalStorageState(defaultState);
		return defaultState;
	}, [defaultWidth, defaultCollapsed]);

	const [sideNavState, setSideNavState] = useReducer(reducer, initState);

	const onCollapseHandler = useCallback<CollapseCallback>(
		(args) => {
			createAnalyticsEvent({
				eventType: UI_EVENT_TYPE,
				action: 'collapsed',
				actionSubject: 'sideNavigation',
				attributes: {},
			}).fire(ANALYTICS_CHANNEL);

			setSideNavState({
				defaultCollapsed: true,
			});
			onCollapse?.(args);
		},
		[onCollapse, createAnalyticsEvent],
	);

	const onExpandHandler = useCallback<ExpandCallback>(
		(args) => {
			createAnalyticsEvent({
				eventType: UI_EVENT_TYPE,
				action: 'expanded',
				actionSubject: 'sideNavigation',
				attributes: {},
			}).fire(ANALYTICS_CHANNEL);

			setSideNavState({
				defaultCollapsed: false,
			});
			onExpand?.(args);
		},
		[onExpand, createAnalyticsEvent],
	);

	const onResizeStartHandler = useCallback<ResizeStartCallback>(
		(args) => {
			createAnalyticsEvent({
				eventType: UI_EVENT_TYPE,
				action: 'resizeStart',
				actionSubject: 'sideNavigation',
				attributes: {},
			}).fire(ANALYTICS_CHANNEL);

			onResizeStart?.(args);
		},
		[onResizeStart, createAnalyticsEvent],
	);

	const onResizeEndHandler = useCallback<ResizeEndCallback>(
		(args) => {
			createAnalyticsEvent({
				eventType: UI_EVENT_TYPE,
				action: 'resizeEnd',
				actionSubject: 'sideNavigation',
				attributes: {},
			}).fire(ANALYTICS_CHANNEL);

			setSideNavState({
				defaultWidth: args.finalWidth,
			});
			onResizeEnd?.(args);
		},
		[onResizeEnd, createAnalyticsEvent],
	);

	return (
		<SideNavVisibilityContext.Provider
			value={{
				defaultWidth: sideNavState.defaultWidth,
				defaultCollapsed: sideNavState.defaultCollapsed,
				onExpand: onExpandHandler,
				onCollapse: onCollapseHandler,
				onResizeEnd: onResizeEndHandler,
				onResizeStart: onResizeStartHandler,
			}}
		>
			{children}
		</SideNavVisibilityContext.Provider>
	);
}

function reducer(state: SideNavState, payload: Partial<SideNavState>): SideNavState {
	const newState = { ...state, ...payload };
	// Update localStorage with the new state
	setLocalStorageState(newState);
	return newState;
}

function setLocalStorageState(state: SideNavState) {
	localStorage.setItem(NAV_STATE_LS_KEY, JSON.stringify(state));
}

function getLocalStorageState(state: string | null) {
	if (!state) {
		return;
	}

	try {
		const parsedState = JSON.parse(state);
		if (
			parsedState &&
			typeof parsedState.defaultWidth === 'number' &&
			typeof parsedState.defaultCollapsed === 'boolean'
		) {
			return parsedState satisfies SideNavState;
		}
	} catch (error) {
		// something went wrong with parsing, return undefined
	}
}
